import React, { useState, useEffect } from 'react';
import '../../pages/Survey/AddSurveyForm.css';
import { CircularProgress } from '@mui/material';
import apiService from '../../services/api';
import PropTypes from 'prop-types';

const close = apiService.getBlobAssetUrl('close_popup.svg');

const AddSurveyTypeForm = ({ onClose, onSave, setSnackbarOpen, setSnackbarMessage, editData, setFilterType }) => {
  const isEditing = !!editData?.surveytype_id; // Determine if in edit mode
  const [formData, setFormData] = useState({
    type: '',
    description: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        type: editData.surveytype_name || '',
        description: editData.surveytype_description || '',
      });
      console.log('Edit data loaded:', editData); // Debug: Verify editData content
    } else {
      setFormData({
        type: '',
        description: '',
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Changing ${name} to ${value}`);
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      console.log('Updated formData:', newData);
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nameFormatRegex = /^[a-zA-Z0-9_\-\s]*$/;

    if (!formData.type.trim()) {
      setSnackbarMessage('Survey type name is required');
      setSnackbarOpen(true);
      return;
    }
    const trimmedType = formData.type.trim();

if (!trimmedType) {
  setSnackbarMessage('Survey type name is required');
  setSnackbarOpen(true);
  return;
}

if (!nameFormatRegex.test(trimmedType)) {
  setSnackbarMessage('Survey type name cannot contain special characters.');
  setSnackbarOpen(true);
  return;
}


    setIsLoading(true);

    const accessToken = process.env.REACT_APP_ACCESS_TOKEN;
    const payload = {
      surveytype_name: formData.type.trim(),
      surveytype_description: formData.description.trim(),
    };

    try {
      // Step 1: Fetch all survey types to check for duplicates
      const existingSurveyTypes = await apiService.fetchData('surveys_types', accessToken);
      const surveyTypesList = existingSurveyTypes?.results || existingSurveyTypes || [];

      const newSurveyTypeName = formData.type.trim().toLowerCase();
      let surveyTypeExists = false;

      if (Array.isArray(surveyTypesList)) {
        surveyTypeExists = surveyTypesList.some((surveyType) => {
          const existingName = surveyType.surveytype_name?.toLowerCase() || surveyType.name?.toLowerCase();
          const existingId = surveyType.surveytype_id || surveyType.id;

          // For editing: allow same name if it's the same record
          if (isEditing) {
            return existingName === newSurveyTypeName && existingId !== editData.surveytype_id;
          } else {
            return existingName === newSurveyTypeName;
          }
        });
      }

      if (surveyTypeExists) {
        setSnackbarMessage('This survey type already exists.');
        setSnackbarOpen(true);
        setIsLoading(false);
        return;
      }

      if (isEditing) {
        if (formData.description && formData.description.trim() === '') {
        setSnackbarMessage('Description cannot be just spaces.');
        setSnackbarOpen(true);
        return;
        }
        // Proceed with update
        const payloadedit = {
          surveytype_id: editData.surveytype_id,
          surveytype_name: formData.type.trim(),
          surveytype_description: formData.description.trim(), // Explicitly ensure description is included
        };

        console.log('Sending update payload:', payloadedit); // Debug: Verify payload
        const response = await apiService.putData('surveys_types', payloadedit, accessToken);
        console.log("PUT response:", response);

        // Check if backend responds with duplicate error
        if (response?.message?.toLowerCase().includes('already exists')) {
          setSnackbarMessage('This survey type already exists.');
          setSnackbarOpen(true);
          setIsLoading(false);
          return;
        }

        setSnackbarMessage('Survey type updated successfully!');
      } else {
        // Proceed with create
        console.log('Sending create payload:', payload); // Debug: Verify payload
        const response = await apiService.postData('surveys_types', payload, accessToken);
        console.log('Post Response:', response);

        // Check if backend responds with duplicate error
        if (response?.message?.toLowerCase().includes('already exists')) {
          setSnackbarMessage('This survey type already exists.');
          setSnackbarOpen(true);
          setIsLoading(false);
          return;
        }

        setSnackbarMessage('Survey type added successfully!');
      }

      setSnackbarOpen(true);
      onSave();
      if (setFilterType) {
        setFilterType(payload.surveytype_name);
      }
      onClose();
    } catch (error) {
      console.error('API Error:', error.response || error.message);
      const errorMessage = error.response?.data?.message || error.message;
      setSnackbarMessage(`Failed to ${isEditing ? 'update' : 'add'} survey type: ${errorMessage}`);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div id="survey-form-modal1">
      <div className="survey-type-modal-content">
        <div className="survey-form-modal-header">
          <h2>{isEditing ? 'Update Survey Type' : 'Add Survey Type'}</h2>
          <button className="survey-form-close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="survey-form-scrollable-content">
          <form onSubmit={handleSubmit}>
            <div className="survey-form-form-group">
              <label htmlFor="type">Type <span style={{ color: 'red' }}>*</span></label>
              <input 
                type="text"
                name="type"
                value={formData.type}
                onChange={handleChange}
                placeholder="Enter Survey Type (Ex:CR CSAT-Resource TOD)"
                required
              />
            </div>

            <div className="survey-form-form-group">
              <label htmlFor="description">Description <span style={{ color: 'red' }}>*</span></label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                placeholder="Enter Survey Description (Ex:TOD Resource Evaluation)"
              />
            </div>

            <div className="survey-form-form-actions">
              <button type="button" className="survey-form-cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="survey-form-save-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <CircularProgress size={20} id="CircularProgress" />
                    {isEditing ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  isEditing ? 'Update' : 'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// PropTypes validation
AddSurveyTypeForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  setSnackbarOpen: PropTypes.func.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
  editData: PropTypes.object, // Corrected from PropTypes.func to PropTypes.object
  setFilterType: PropTypes.func,
};

export default AddSurveyTypeForm;
